import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Icon } from "../components/ui/Icon.js";
import {
  createExchangeKey,
  deleteExchangeKey,
  fetchExchangeKeys,
  type ExchangeKeyRow,
} from "../lib/api.js";

const EXCHANGES = [
  "binance",
  "bybit",
  "okx",
  "kraken",
  "coinbase",
  "kucoin",
  "gateio",
  "bitget",
] as const;

function fmtDate(ms: number | null): string {
  return ms ? new Date(ms).toLocaleString() : "—";
}

export function ExchangeKeysPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["exchange-keys"], queryFn: fetchExchangeKeys });
  const [open, setOpen] = useState(false);

  const remove = useMutation({
    mutationFn: (id: string) => deleteExchangeKey(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["exchange-keys"] }),
  });

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Exchange API Keys</div>
          <div className="muted" style={{ fontSize: 11 }}>
            AES-256-GCM encrypted at rest · withdrawal scope is never accepted
          </div>
        </div>
        <button
          type="button"
          className="se-btn se-btn-primary"
          style={{ marginLeft: "auto" }}
          onClick={() => setOpen(true)}
        >
          <Icon name="plus" size={13} /> Add key
        </button>
      </div>

      <div className="se-card">
        <table className="se-table">
          <thead>
            <tr>
              <th>Exchange</th>
              <th>API Key</th>
              <th>Scopes</th>
              <th>Last tested</th>
              <th>Added</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(q.data ?? []).length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 24, textAlign: "center" }} className="muted">
                  No exchange keys connected. Add one to start syncing your spot
                  portfolio and (optionally) place trades.
                </td>
              </tr>
            ) : (
              (q.data ?? []).map((k: ExchangeKeyRow) => (
                <tr key={k.id}>
                  <td style={{ fontWeight: 500 }}>{k.exchange}</td>
                  <td className="mono">{k.keyLabel}</td>
                  <td>
                    {k.scopeRead ? (
                      <span className="se-pill se-pill-primary" style={{ marginRight: 4 }}>READ</span>
                    ) : null}
                    {k.scopeTrade ? (
                      <span className="se-pill se-pill-buy">TRADE</span>
                    ) : null}
                  </td>
                  <td className="muted" style={{ fontSize: 11 }}>{fmtDate(k.lastTestedAt)}</td>
                  <td className="muted" style={{ fontSize: 11 }}>{fmtDate(k.createdAt)}</td>
                  <td>
                    <button
                      type="button"
                      className="se-btn se-btn-ghost se-btn-sm"
                      onClick={() => {
                        if (window.confirm(`Delete ${k.exchange} key?`)) remove.mutate(k.id);
                      }}
                    >
                      <Icon name="x" size={12} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {open ? (
        <AddKeyModal
          onClose={() => setOpen(false)}
          onCreated={() => {
            setOpen(false);
            qc.invalidateQueries({ queryKey: ["exchange-keys"] });
          }}
        />
      ) : null}
    </div>
  );
}

function AddKeyModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [exchange, setExchange] = useState<string>("binance");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [scopeRead, setScopeRead] = useState(true);
  const [scopeTrade, setScopeTrade] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await createExchangeKey({ exchange, apiKey, apiSecret, scopeRead, scopeTrade });
      onCreated();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <div className="se-card" style={{ width: 460, padding: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Add exchange API key</div>

        <label className="muted" style={{ fontSize: 11, display: "block" }}>Exchange</label>
        <select className="se-input" value={exchange} onChange={(e) => setExchange(e.target.value)} style={{ marginBottom: 10 }}>
          {EXCHANGES.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>

        <label className="muted" style={{ fontSize: 11, display: "block" }}>API Key</label>
        <input className="se-input mono" value={apiKey} onChange={(e) => setApiKey(e.target.value)} style={{ marginBottom: 10 }} />

        <label className="muted" style={{ fontSize: 11, display: "block" }}>API Secret</label>
        <input className="se-input mono" type="password" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} style={{ marginBottom: 12 }} />

        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={scopeRead} onChange={(e) => setScopeRead(e.target.checked)} /> Read portfolio
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={scopeTrade} onChange={(e) => setScopeTrade(e.target.checked)} /> Spot trade
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-faint)" }}>
            <input type="checkbox" disabled /> Withdrawal (never)
          </label>
        </div>

        <div
          style={{
            background: "rgba(168, 85, 247, 0.06)",
            border: "1px solid rgba(168, 85, 247, 0.25)",
            padding: "8px 10px",
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 11,
            marginBottom: 14,
          }}
        >
          <Icon name="lock" size={13} className="ai-text" />
          <span style={{ color: "var(--text-mid)" }}>
            Stored as AES-256-GCM ciphertext. Your secret is never displayed again after this step.
          </span>
        </div>

        {error ? <div style={{ color: "var(--sell)", fontSize: 11, marginBottom: 10 }}>{error}</div> : null}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="se-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="se-btn se-btn-primary" disabled={submitting} onClick={submit}>
            {submitting ? "Saving…" : "Save key"}
          </button>
        </div>
      </div>
    </div>
  );
}
